import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  ProgressBar,
  Accordion,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCalculator,
  FaFileAlt,
  FaDollarSign,
  FaRulerCombined,
  FaChartLine,
  FaCheckCircle,
  FaSync,
  FaCalendarAlt,
  FaTruck,
  FaClipboardCheck,
  FaHardHat,
  FaIndustry,
  FaRegListAlt,
} from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

const BidEstimateList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [bidTasks, setBidTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Removed projectInfo state since we don't have the API

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  const fetchBidTasks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/data/getBidTasksByProject/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setBidTasks(res.data.bidTasks || []);
        // Removed the project info fetching since API doesn't exist
      }
    } catch (error) {
      console.error("Fetch bid tasks error:", error);
      toast.error("Failed to load bid tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchBidTasks();
    }
  }, [projectId]);

  const calculateTotals = () => {
    return bidTasks.reduce(
      (totals, task) => ({
        totalAmount: totals.totalAmount + (task.totalAmount || 0),
        totalSqft: totals.totalSqft + (task.totalSqft || 0),
        actualCost: totals.actualCost + (task.actualCost || 0),
      }),
      { totalAmount: 0, totalSqft: 0, actualCost: 0 }
    );
  };

  const calculateProfit = () => {
    const totals = calculateTotals();
    return totals.totalAmount - totals.actualCost;
  };

  const calculateProfitMargin = () => {
    const totals = calculateTotals();
    if (totals.totalAmount === 0) return 0;
    return (
      ((totals.totalAmount - totals.actualCost) / totals.totalAmount) * 100
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const navigateToTaskPage = (taskId, pageType) => {
    switch (pageType) {
      case "schedule":
        navigate(`/bid-tasks/schedule/${taskId}`);
        break;
      case "vendors":
        navigate(`/bid-tasks/vendors-utility/${taskId}`);
        break;
      case "punchlist":
        navigate(`/bid-tasks/punch-list/${taskId}`);
        break;
      
      case "action-schedule":
        navigate(`/bid-tasks/action-schedule/${taskId}`);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading bid estimates...</span>
      </div>
    );
  }

  const totals = calculateTotals();
  const profit = calculateProfit();
  const profitMargin = calculateProfitMargin();

  return (
    <Container
      fluid
      className="py-4"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Project List
              </Button>
              <h2 className="fw-bold mb-2">
                <FaCalculator className="me-3" />
                Bid Estimates
              </h2>
              <div className="d-flex align-items-center">
                <Badge bg="info" className="px-3 py-2 me-3">
                  <FaFileAlt className="me-2" />
                  {bidTasks.length} Task{bidTasks.length !== 1 ? "s" : ""}
                </Badge>
                {/* Removed project info display since we don't have the API */}
                <small className="text-muted">
                  Project ID: {projectId?.substring(0, 16)}...
                </small>
              </div>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={fetchBidTasks}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center">
                  <div className="mb-2">
                    <FaDollarSign size={24} className="text-success" />
                  </div>
                  <div className="fw-bold fs-4 text-success">
                    {formatCurrency(totals.totalAmount)}
                  </div>
                  <small className="text-muted">Total Estimate</small>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center">
                  <div className="mb-2">
                    <FaRulerCombined size={24} className="text-primary" />
                  </div>
                  <div className="fw-bold fs-4">
                    {totals.totalSqft.toLocaleString()} sqft
                  </div>
                  <small className="text-muted">Total Area</small>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center">
                  <div className="mb-2">
                    <FaChartLine size={24} className="text-warning" />
                  </div>
                  <div className="fw-bold fs-4">{formatCurrency(profit)}</div>
                  <small className="text-muted">Estimated Profit</small>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center">
                  <div className="mb-2">
                    <FaCheckCircle size={24} className="text-info" />
                  </div>
                  <div className="fw-bold fs-4">{profitMargin.toFixed(1)}%</div>
                  <small className="text-muted">Profit Margin</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {bidTasks.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaCalculator size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Bid Tasks Found</h4>
            <p className="text-muted mb-4">
              No bid tasks have been added to this project yet.
            </p>
            <Button
              variant="primary"
              onClick={fetchBidTasks}
              className="d-flex align-items-center mx-auto"
            >
              <FaSync className="me-2" />
              Refresh
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Accordion className="mb-4">
          {bidTasks.map((task, index) => {
            const profit = (task.totalAmount || 0) - (task.actualCost || 0);
            const profitMargin =
              task.totalAmount > 0 ? (profit / task.totalAmount) * 100 : 0;

            return (
              <Accordion.Item key={task._id} eventKey={index.toString()}>
                <Accordion.Header>
                  <div className="d-flex w-100 align-items-center">
                    <div className="me-3">
                      <Badge bg="light" text="dark" className="px-2 py-1">
                        {index + 1}
                      </Badge>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="fw-medium">
                          {task.taskName?.taskName || "Unnamed Task"}
                        </div>
                        <div className="text-muted small me-3">
                          {formatCurrency(task.totalAmount || 0)} •{" "}
                          {task.totalSqft?.toLocaleString() || 0} sqft
                        </div>
                      </div>
                      <div className="small text-muted">
                        ID: {task._id?.substring(0, 12)}... • Created:{" "}
                        {formatDate(task.createdAt)} by {task.createdBy?.name}
                      </div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row className="mb-3">
                    <Col md={3}>
                      <Card className="h-100">
                        <Card.Body>
                          <h6 className="text-muted mb-3">Financial Details</h6>
                          <div className="mb-2">
                            <small className="text-muted">Rate per Sqft</small>
                            <div className="fw-bold">
                              {formatCurrency(task.ratePerSqft || 0)}
                            </div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">Total Sqft</small>
                            <div className="fw-bold">
                              {task.totalSqft?.toLocaleString() || 0} sqft
                            </div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">Total Amount</small>
                            <div className="fw-bold text-success">
                              {formatCurrency(task.totalAmount || 0)}
                            </div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">Actual Cost</small>
                            <div
                              className={
                                task.actualCost > 0
                                  ? "fw-semibold"
                                  : "text-muted"
                              }
                            >
                              {task.actualCost > 0
                                ? formatCurrency(task.actualCost)
                                : "Not Set"}
                            </div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">Profit</small>
                            <div
                              className={`fw-bold ${
                                profit >= 0 ? "text-success" : "text-danger"
                              }`}
                            >
                              {formatCurrency(profit)}
                            </div>
                            <ProgressBar
                              now={Math.min(Math.max(profitMargin, 0), 100)}
                              variant={
                                profitMargin >= 20
                                  ? "success"
                                  : profitMargin >= 0
                                  ? "warning"
                                  : "danger"
                              }
                              className="mt-1"
                              style={{ height: "4px" }}
                            />
                            <small className="text-muted">
                              {profitMargin.toFixed(1)}% margin
                            </small>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={9}>
                      <Card className="h-100">
                        <Card.Body>
                          <h6 className="text-muted mb-3">Task Actions</h6>
                          <div className="row g-2">
                            <div className="col-6">
                              <Button
                                variant="outline-primary"
                                onClick={() =>
                                  navigateToTaskPage(task._id, "schedule")
                                }
                                className="w-100 mb-2 p-3 d-flex align-items-center justify-content-center"
                              >
                                <FaCalendarAlt className="me-2" />
                                Schedule Task
                              </Button>
                            </div>
                            <div className="col-6">
                              <Button
                                variant="outline-success"
                                onClick={() =>
                                  navigateToTaskPage(task._id, "vendors")
                                }
                                className="w-100 mb-2 p-3 d-flex align-items-center justify-content-center"
                              >
                                <FaTruck className="me-2" />
                                Vendors & Utility
                              </Button>
                            </div>

                            <div className="col-6">
                              <Button
                                variant="outline-warning"
                                onClick={() =>
                                  navigateToTaskPage(task._id, "punchlist")
                                }
                                className="w-100 mb-2 p-3 d-flex align-items-center justify-content-center"
                              >
                                <FaClipboardCheck className="me-2" />
                                Punch List
                              </Button>
                            </div>
                            

                            <div className="col-6">
                              <Button
                                variant="outline-dark"
                                onClick={() =>
                                  navigateToTaskPage(
                                    task._id,
                                    "action-schedule"
                                  )
                                }
                                className="w-100 mb-2 p-3 d-flex align-items-center justify-content-center"
                              >
                                <FaIndustry className="me-2" />
                                Action Schedules
                              </Button>
                            </div>
                          </div>
                          
                          {/* Added task ID display for debugging */}
                          <div className="mt-3 pt-3 border-top">
                            <small className="text-muted d-block">Task ID</small>
                            <code className="small">{task._id}</code>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {bidTasks.length > 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="bg-light">
            <Row className="align-items-center">
              <Col md={4}>
                <div className="small text-muted">
                  Showing {bidTasks.length} task{bidTasks.length !== 1 ? "s" : ""}
                  <div className="mt-1">
                    Project ID: {projectId?.substring(0, 16)}...
                  </div>
                </div>
              </Col>
              <Col md={8}>
                <div className="d-flex justify-content-end gap-4">
                  <div className="text-center">
                    <div className="fw-bold text-success">
                      {formatCurrency(totals.totalAmount)}
                    </div>
                    <small className="text-muted">Total Estimate</small>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold">
                      {formatCurrency(totals.actualCost)}
                    </div>
                    <small className="text-muted">Total Actual Cost</small>
                  </div>
                  <div className="text-center">
                    <div
                      className={`fw-bold ${
                        profit >= 0 ? "text-success" : "text-danger"
                      }`}
                    >
                      {formatCurrency(profit)}
                    </div>
                    <small className="text-muted">Net Profit</small>
                  </div>
                  <div className="text-center">
                    <div
                      className={`fw-bold ${
                        profitMargin >= 20
                          ? "text-success"
                          : profitMargin >= 0
                          ? "text-warning"
                          : "text-danger"
                      }`}
                    >
                      {profitMargin.toFixed(1)}%
                    </div>
                    <small className="text-muted">Avg. Profit Margin</small>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default BidEstimateList;